import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldCheck, User, SignOut, Flame } from "@phosphor-icons/react";

export default function Header() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [logo, setLogo] = useState(null);
    const [streak, setStreak] = useState(null);

    useEffect(() => {
        const load = () => api.get("/settings").then((r) => setLogo(r.data?.logo_data_url || null)).catch(() => {});
        load();
        const onUpdate = () => load();
        window.addEventListener("logo-updated", onUpdate);
        return () => window.removeEventListener("logo-updated", onUpdate);
    }, []);

    useEffect(() => {
        if (!user) { setStreak(null); return; }
        const load = () => api.get("/user/streak").then((r) => setStreak(r.data)).catch(() => {});
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [user?.id]);

    const showNudge = user && streak && streak.current_streak > 0 && !streak.done_today;
    const remaining = streak ? Math.max(0, (streak.target || 10) - (streak.questions_today || 0)) : 0;

    return (
        <header className="glass sticky top-0 z-40 w-full" data-testid="app-header">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
                <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
                    {logo ? (
                        <img src={logo} alt="Logo" className="h-9 w-auto max-w-[160px] rounded-sm object-contain" data-testid="brand-logo" />
                    ) : (
                        <div className="flex h-9 w-9 items-center justify-center rounded-sm border border-primary/40 bg-primary/10 text-primary">
                            <ShieldCheck weight="duotone" size={22} />
                        </div>
                    )}
                    {!logo && (
                        <div className="leading-tight">
                            <div className="font-display text-lg font-semibold tracking-tight">CRMA Vault</div>
                            <div className="overline text-muted-foreground">Exam Prep · IIA Aligned</div>
                        </div>
                    )}
                </Link>

                <nav className="hidden items-center gap-8 md:flex">
                    <Link to="/#features" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-features">Domains</Link>
                    <Link to="/discover" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-discover">Discover</Link>
                    <Link to="/#pricing" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-pricing">Pricing</Link>
                    {user && (
                        <>
                            <Link to="/practice" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-practice">Practice</Link>
                            <Link to="/exam" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-exam">Exam</Link>
                            <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground" data-testid="nav-dashboard">Dashboard</Link>
                            {user.role === "admin" && (
                                <Link to="/admin" className="text-sm text-primary hover:text-primary/80" data-testid="nav-admin">Admin</Link>
                            )}
                        </>
                    )}
                </nav>

                <div className="flex items-center gap-3">
                    {showNudge && (
                        <Link
                            to="/practice"
                            className="hidden items-center gap-2 rounded-full border border-primary/50 bg-primary/10 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-primary/20 md:inline-flex"
                            data-testid="streak-nudge"
                            title={`You've kept a ${streak.current_streak}-day streak going — finish today's warmup to protect it.`}
                        >
                            <Flame weight="fill" size={14} className="text-primary" />
                            <span>
                                <strong>{streak.current_streak}-day streak</strong> · {remaining} question{remaining === 1 ? "" : "s"} to save it
                            </span>
                        </Link>
                    )}
                    {user ? (
                        <>
                            <div className="hidden items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs md:flex">
                                <User weight="duotone" size={16} className="text-primary" />
                                <span data-testid="user-email">{user.email}</span>
                                {user.premium && <span className="ml-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">Premium</span>}
                            </div>
                            <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn">
                                <SignOut weight="duotone" size={16} className="mr-1" /> Logout
                            </Button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" data-testid="header-login-link">
                                <Button variant="ghost" size="sm">Log in</Button>
                            </Link>
                            <Link to="/register" data-testid="header-register-link">
                                <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90">Get started</Button>
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
